
function formatUrlTemplate(url: string, values: Record<string, string | number>) {
    return Object.keys(values).reduce((previousValue, x) => {
        return previousValue.replace("{" + x + "}", encodeURIComponent(values[x] as string));
    }, url);
}

export const ApiRoutes = {
    apiKey: {
        getApiKeys: (values: { domainName: string }) => formatUrlTemplate("/api/v1/api-keys/{domainName}", values),
        createApiKey: () => "/api/v1/api-keys/create",
        modifyApiKey: () => "/api/v1/api-keys/modify",
        deactivateApiKey: (values: { domainName: string, apiKeyId: string }) => formatUrlTemplate("/api/v1/api-keys/deactivate/{domainName}/{apiKeyId}", values)
    },
    auditLog: {
        getAuditLogSyncCredentials: () => "/api/v1/audit-log/sync-credentials",
        getAuditLogSyncCredentialsV2: () => "/api/v2/audit-log/sync-credentials",
        listAuditLogs: (values: { domain: string, productId: string, nodeTag: string, date: string }) => formatUrlTemplate("/api/v1/audit-log/list/{domain}/{productId}/{nodeTag}/{date}", values)
    },
    backup: {
        getProducts: (values: { domainName: string }) => formatUrlTemplate("/api/v1/backup/products/{domainName}", values),
        getBackupCredentials: () => "/api/v1/backup/credentials",
        getBackupCredentialsV2: () => "/api/v2/backup/credentials",
        getBackupCredentialsFromLink: () => "/backup/links",
        getBackupCredentialsFromLinkV2: () => "/backup/links",
        clearBackupCredentialsLinks: (values: { domainName: string }) => formatUrlTemplate("/api/v1/backup/links/{domainName}", values),
        getReadonlyBackupCredentialsLink: (values: { domainName: string }) => formatUrlTemplate("/api/v1/backup/link/{domainName}", values),
        getEncryptionKeyForBackups: (values: { domainName: string }) => formatUrlTemplate("/api/v1/backup/encryption-key/{domainName}", values),
        getBackupLinksInfo: (values: { domainName: string }) => formatUrlTemplate("/api/v1/backup/links-info/{domainName}", values),
        listBackupDirectory: (values: { domainName: string }) => formatUrlTemplate("/api/v1/backup/list-directory/{domainName}", values),
        getBackupDownloadLinks: (values: { domainName: string }) => formatUrlTemplate("/api/v1/backup/download-links/{domainName}", values)
    },
    billing: {
        billingSummary: (values: { domainName: string }) => formatUrlTemplate("/api/v1/billing/summary/{domainName}", values),
        billingSummaryList: (values: { domainName: string }) => formatUrlTemplate("/api/v1/billing/summary/{domainName}/list", values),
        invoiceDetails: (values: { domainName: string, invoiceId: string }) => formatUrlTemplate("/api/v1/billing/invoice-details/{domainName}/{invoiceId}", values),
        costsDetails: (values: { domainName: string }) => formatUrlTemplate("/api/v1/billing/costs-details/{domainName}", values),
        chargeUpToPreviousBillingPeriod: (values: { domainName: string }) => formatUrlTemplate("/api/v1/billing/charge-up-to-previous-billing-period/{domainName}", values)
    },
    clusterHealth: {
        getClusterHealthIncidents: () => "/api/v1/cluster-health/incidents",
        getClusterHealthSuggestions: () => "/api/v1/cluster-health/suggestions"
    },
    configuration: {
        getConfiguration: () => "/api/v1/configuration"
    },
    cpuCredits: {
        getCpuCredits: () => "/api/v1/cpu-credits/remaining",
        getCpuCreditsV2: () => "/api/v2/cpu-credits/remaining",
        getCpuCreditsHistory: (values: { domainName: string }) => formatUrlTemplate("/api/v1/cpu-credits/history/{domainName}", values)
    },
    domain: {
        isAvailable: () => "/api/v1/domain/is-available",
        isSubdomainAvailable: () => "/api/v1/domain/is-subdomain-available"
    },
    internalForward: {
        requestSupport: () => "/request-support",
        terms: () => "/terms",
        privacyPolicy: () => "/privacy-policy",
        supportPolicy: () => "/support-policy",
        mainPage: () => "/"
    },
    invitation: {
        getInvitationInfo: () => "/api/v1/invitation-info",
        invitationLink: () => "/api/v1/invitation"
    },
    login: {
        loginLink: () => "/api/v1/login/link",
        loginUser: () => "/api/v1/login",
        logout: () => "/api/v1/logout"
    },
    metrics: {
        getMetrics: () => "/api/v1/metrics/get-metric/"
    },
    nodeStartup: {
        onNodeStartup: () => "/api/v1/node/startup",
        onNodeStartupV2: () => "/api/v2/node/startup"
    },
    nodeStorageUpgrade: {
        extendVolume: () => "/api/v1/node/storage/auto-extend",
        extendVolumeV2: () => "/api/v2/node/storage/auto-extend"
    },
    paymentInfo: {
        addBillingInfoForExistingAccount: (values: { domainName: string }) => formatUrlTemplate("/api/v1/payment-info/add-billing-info/{domainName}", values),
        changeInvoiceDetails: (values: { domainName: string }) => formatUrlTemplate("/api/v1/payment-info/change-invoice-details/{domainName}", values),
        setCustomInvoiceRecipientEmail: (values: { domainName: string }) => formatUrlTemplate("/api/v1/payment-info/set-custom-invoice-recipient-email/{domainName}", values),
        addCreditCard: (values: { domainName: string }) => formatUrlTemplate("/api/v1/payment-info/add-credit-card/{domainName}", values),
        changeActiveCreditCard: (values: { domainName: string }) => formatUrlTemplate("/api/v1/payment-info/change-active-credit-card/{domainName}", values),
        removeCreditCard: (values: { domainName: string }) => formatUrlTemplate("/api/v1/payment-info/remove-credit-card/{domainName}", values)
    },
    pricing: {
        getPricingList: () => "/api/v1/pricing",
        calculatePrice: () => "/api/v1/pricing/calculate-price",
        calculateInternalPrice: () => "/api/v1/pricing/calculate-internal-price"
    },
    products: {
        changeProduct: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/change-product/{domainName}", values),
        getProducts: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/get-products/{domainName}", values),
        getProductsStatus: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/status/{domainName}", values),
        getPaymentContext: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/payment-context/{domainName}", values),
        createProduct: () => "/api/v1/products/create",
        restartNode: (values: { domainName: string, id: string, nodeTag: string }) => formatUrlTemplate("/api/v1/products/restart-node/{domainName}/{id}/{nodeTag}", values),
        enableFeature: (values: { feature: string, domainName: string, id: string }) => formatUrlTemplate("/api/v1/products/feature/{feature}/{domainName}/{id}", values),
        disableFeature: (values: { feature: string, domainName: string, id: string }) => formatUrlTemplate("/api/v1/products/feature/{feature}/{domainName}/{id}", values),
        getProduct: (values: { domainName: string, id: string }) => formatUrlTemplate("/api/v1/products/get/{domainName}/{id}", values),
        getClientCertificate: (values: { domainName: string, id: string }) => formatUrlTemplate("/api/v1/products/client-certificate/{domainName}/{id}", values),
        regenerateClientCertificate: (values: { domainName: string, id: string }) => formatUrlTemplate("/api/v1/products/client-certificate/regenerate/{domainName}/{id}", values),
        getCreateContext: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/create/context/{domainName}", values),
        validateCidrs: () => "/api/v1/products/validate-cidr",
        updateAllowedIps: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/update-allowed-ips/{domainName}", values),
        updateMonitoringIps: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/update-monitoring-ips/{domainName}", values),
        rollSnmpCredentials: (values: { domainName: string, id: string }) => formatUrlTemplate("/api/v1/products/roll-snmp-credentials/{domainName}/{id}", values),
        updateFeatureConfiguration_PowerBi: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/features/configure/power-bi/{domainName}", values),
        updateFeatureConfiguration_Autoscaling: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/features/configure/autoscaling/{domainName}", values),
        updateFeatureConfiguration_MonitoringOpenTelemetry: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/features/configure/monitoring/open-telemetry/{domainName}/", values),
        installTools: (values: { domainName: string, id: string }) => formatUrlTemplate("/api/v1/products/install-tools/{domainName}/{id}", values),
        terminateProduct: (values: { domainName: string, id: string }) => formatUrlTemplate("/api/v1/products/terminate/{domainName}/{id}", values),
        deleteProduct: (values: { domainName: string, id: string }) => formatUrlTemplate("/api/v1/products/delete/{domainName}/{id}", values),
        changeVolume: (values: { domainName: string }) => formatUrlTemplate("/api/v1/products/change-volume/{domainName}", values),
        addAdditionalNode: (values: { domainName: string, id: string }) => formatUrlTemplate("/api/v1/products/additional-node/add/{domainName}/{id}", values),
        removeAdditionalNode: (values: { domainName: string, id: string, nodeTag: string }) => formatUrlTemplate("/api/v1/products/additional-node/remove/{domainName}/{id}/{nodeTag}", values)
    },
    profile: {
        getConfiguration: () => "/api/v1/profile/configuration",
        updatePhoneNumber: () => "/api/v1/profile/update-phone-number"
    },
    support: {
        supportRequest: () => "/api/v1/support/request",
        supportContractRequest: () => "/api/v1/support/support-contract-request",
        loadContext: (values: { domainName: string }) => formatUrlTemplate("/api/v1/support/load-context/{domainName}", values)
    },
    twoFactorAuthentication: {
        generateCode: () => "/api/v1/2fa/generate-code",
        getLoginStage: () => "/api/v1/2fa/login-stage",
        verifyAppCode: () => "/api/v1/2fa/verify-app-code",
        loginWithRecoveryCode: () => "/api/v1/2fa/login-with-recovery-code",
        loginWith2Fa: () => "/api/v1/2fa/login",
        disable2Fa: () => "/api/v1/2fa/disable",
        generateRecoveryCodes: () => "/api/v1/2fa/generate-recovery-codes",
        forgetBrowser: () => "/api/v1/2fa/forget-browser"
    },
    users: {
        getToken: () => "/api/v1/user/get-token",
        getUserInfo: () => "/api/v1/user",
        inviteUser: (values: { domainName: string }) => formatUrlTemplate("/api/v1/users/invite/{domainName}", values),
        confirmInvitation: (values: { domainName: string }) => formatUrlTemplate("/api/v1/users/confirm-invitation/{domainName}", values),
        createUserViaInvitation: () => "/api/v1/users/create-via-invitation",
        cancelPendingInvitation: (values: { domainName: string }) => formatUrlTemplate("/api/v1/users/cancel-invitation/{domainName}", values),
        unlinkUserFromAccount: (values: { domainName: string }) => formatUrlTemplate("/api/v1/users/unlink/{domainName}", values),
        getAccountInfo: (values: { domainName: string }) => formatUrlTemplate("/api/v1/account/get-info/{domainName}", values),
        getAccountBillingInfo: (values: { domainName: string }) => formatUrlTemplate("/api/v1/account/get-billing-info/{domainName}", values),
        getAccountUsersInfo: (values: { domainName: string }) => formatUrlTemplate("/api/v1/account/get-users-info/{domainName}", values),
        createUser: () => "/api/v1/users/create",
        isEmailAddressAlreadyUsed: () => "/api/v1/users/is-email-already-used",
        getDashboardUserInfo: (values: { domainName: string }) => formatUrlTemplate("/api/v1/dashboard-user-info/{domainName}", values)
    },
    version: {
        sha1: () => "/api/v1/version/sha1"
    },
    azureMarketplaceLandingPage: {
        unpackToken: () => "/api/v1/azure-marketplace/unpack-token",
        registerSubscription: () => "/api/v1/azure-marketplace/register-subscription"
    },
    azureMarketplaceWebhook: {
        run: () => "/api/v1/azure-marketplace/webhook"
    },
    awsMarketplaceLandingPage: {
        unpackToken: () => "/api/v1/aws-marketplace/unpack-token",
        registerCustomer: () => "/api/v1/aws-marketplace/register-customer"
    }
};